import React, { useState } from 'react'
import { Resistance } from '../lib/api'
import { X } from 'lucide-react'

interface Props {
  onSubmit: (resistance: Omit<Resistance, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  onCancel: () => void
}

export function ResistanceForm({ onSubmit, onCancel }: Props) {
  const [formData, setFormData] = useState({
    domain: '',
    daily_limit: 5,
    session_duration_minutes: 5,
    is_active: true
  })
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required'
    } else if (!isValidDomain(formData.domain)) {
      newErrors.domain = 'Please enter a valid domain (e.g., instagram.com)'
    }

    if (formData.daily_limit < 1 || formData.daily_limit > 50) {
      newErrors.daily_limit = 'Daily limit must be between 1 and 50'
    }

    if (formData.session_duration_minutes < 1 || formData.session_duration_minutes > 120) {
      newErrors.session_duration_minutes = 'Session duration must be between 1 and 120 minutes'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidDomain = (domain: string) => {
    // Remove protocol and www if present, and clean the domain
    const cleanDomain = domain
      .replace(/^(https?:\/\/)?(www\.)?/, '')
      .split('/')[0]
      .toLowerCase()

    // Basic domain validation
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/
    return domainRegex.test(cleanDomain) && cleanDomain.includes('.')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Form: Submit clicked, formData:', formData)
    
    if (isSubmitting) {
      console.log('Form: Already submitting, ignoring')
      return
    }
    
    if (!validateForm()) {
      console.log('Form: Validation failed, errors:', errors)
      return
    }

    // Clean the domain
    const cleanDomain = formData.domain
      .replace(/^(https?:\/\/)?(www\.)?/, '')
      .split('/')[0]
      .toLowerCase()

    const submissionData = {
      ...formData,
      domain: cleanDomain
    }
    
    console.log('Form: Submitting data:', submissionData)
    setIsSubmitting(true)
    
    try {
      await onSubmit(submissionData)
    } catch (error) {
      console.error('Form: Submit failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, domain: e.target.value }))
    // Clear domain error when user starts typing
    if (errors.domain) {
      setErrors(prev => ({ ...prev, domain: '' }))
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Create New Resistance</h2>
        <button
          onClick={onCancel}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-2">
            Website Domain
          </label>
          <input
            type="text"
            id="domain"
            value={formData.domain}
            onChange={handleDomainChange}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.domain ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="instagram.com"
          />
          {errors.domain && (
            <p className="mt-1 text-sm text-red-600">{errors.domain}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Enter the domain without "http://" or "www" (e.g., instagram.com, twitter.com)
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="daily_limit" className="block text-sm font-medium text-gray-700 mb-2">
              Daily Limit
            </label>
            <input
              type="number"
              id="daily_limit"
              value={formData.daily_limit}
              onChange={(e) => setFormData(prev => ({ ...prev, daily_limit: parseInt(e.target.value) }))}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.daily_limit ? 'border-red-500' : 'border-gray-300'
              }`}
              min="1"
              max="50"
            />
            {errors.daily_limit && (
              <p className="mt-1 text-sm text-red-600">{errors.daily_limit}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              How many times per day you can visit this site
            </p>
          </div>

          <div>
            <label htmlFor="session_duration" className="block text-sm font-medium text-gray-700 mb-2">
              Session Duration
            </label>
            <input
              type="number"
              id="session_duration"
              value={formData.session_duration_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, session_duration_minutes: parseInt(e.target.value) }))}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.session_duration_minutes ? 'border-red-500' : 'border-gray-300'
              }`}
              min="1"
              max="120"
            />
            {errors.session_duration_minutes && (
              <p className="mt-1 text-sm text-red-600">{errors.session_duration_minutes}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Minutes per session before auto-close
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 pt-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm text-gray-700">
            Activate immediately
          </label>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Resistance'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}